import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Stack,
  Switch,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ExternalLink,
  FileCode2,
  LayoutDashboard,
  Lock,
  Plus,
  Trash2,
  TriangleAlert,
  Tv,
  Upload,
} from "lucide-mui";
import { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import FormattedDate from "../../../components/FormattedDate.tsx";
import { keys } from "../../../api/queryKeys.ts";
import { api, getErrorMessage, type MicroApp, type MicroAppVersion } from "../../../api/client.ts";
import CenteredSpinner from "../../../components/CenteredSpinner.tsx";
import DevOnly from "../../../components/DevOnly.tsx";
import EmptyState from "../../../components/EmptyState.tsx";
import RowActions from "../../../components/RowActions.tsx";
import ScopeChip from "../../../components/ScopeChip.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import TableCard from "../../../components/table/TableCard.tsx";
import { useOrganization } from "../../../contexts/OrganizationContext.tsx";
import MicroAppCodeViewerDialog from "./MicroAppCodeViewerDialog.tsx";
import MicroAppGrantsDialog from "./MicroAppGrantsDialog.tsx";
import MicroAppSheetsPanel from "./MicroAppSheetsPanel.tsx";
import MicroAppTvShareDialog from "./MicroAppTvShareDialog.tsx";
import MicroAppUploadDialog from "./MicroAppUploadDialog.tsx";
import PageHeader from "../../../components/PageHeader.tsx";

/** The upload's result; what it left the sandbox to block stays up a while. */
function toastUploadResult(warnings: string[]) {
  if (warnings.length === 0) {
    toast.success("Uploaded");
    return;
  }
  toast(
    <Box>
      Uploaded, but the sandbox will block parts of it:
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {warnings.map((w) => (
          <li key={w}>{w}</li>
        ))}
      </Box>
    </Box>,
    { icon: <TriangleAlert sx={{ color: "warning.main" }} />, duration: 15_000 },
  );
}

/**
 * Operate published micro apps: enable/disable, control nav placement, review
 * the version history and roll back. Publishing normally happens over MCP — an
 * agent authors the app; this page is the human control surface for it. Apps
 * too large for one MCP tool call are uploaded here as files instead.
 */
function MicroAppsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { selectedOrgId } = useOrganization();
  const [selected, setSelected] = useState<MicroApp | null>(null);
  const [grantsFor, setGrantsFor] = useState<MicroApp | null>(null);
  const [tvFor, setTvFor] = useState<MicroApp | null>(null);
  const [codeVersion, setCodeVersion] = useState<MicroAppVersion | null>(null);
  // `undefined` = closed, `null` = creating a new app, an app = new version of it.
  const [uploadTarget, setUploadTarget] = useState<MicroApp | null | undefined>(undefined);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: keys.microApp.all });
  };

  const { data: apps, error, isLoading } = useQuery({
    queryKey: keys.microApp.manageList(selectedOrgId),
    // The manage list returns every app the caller may write, regardless of
    // access_mode — the consumer list would hide restricted apps the caller is
    // not in a granted group for, including apps they administer. Scoped to the
    // globally-selected org.
    queryFn: () => api.getManageableMicroApps(selectedOrgId),
  });

  const { data: versions, isLoading: versionsLoading } = useQuery({
    queryKey: keys.microApp.versions(selected?.id),
    queryFn: () => api.getMicroAppVersions(selected!.id),
    enabled: !!selected,
  });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof api.updateMicroApp>[1] }) =>
      api.updateMicroApp(id, body),
    onSuccess: (app) => {
      setSelected((cur) => (cur && cur.id === app.id ? app : cur));
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (app: MicroApp) => api.deleteMicroApp(app.id),
    onSuccess: (_, app) => {
      setSelected(null);
      invalidate();
      toast.success(`${app.title} deleted`);
    },
  });

  const rows = apps ?? [];

  return (
    <Box>
      <PageHeader
        title="Micro apps"
        description={
          <>
            Dashboards published over MCP or uploaded here. Every publish appends an immutable
            version, so rolling back is always available.
          </>
        }
        actions={
          <Button
            variant="contained"
            startIcon={<Plus fontSize="small" />}
            onClick={() => setUploadTarget(null)}
          >
            New app
          </Button>
        }
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getErrorMessage(error)}
        </Alert>
      ) : !isLoading && rows.length === 0 ? (
        <EmptyState
          variant="panel"
          icon={<LayoutDashboard />}
          title="No micro apps yet"
          description={
            <>
              Ask an agent to call <code>publish_micro_app</code>, or upload the files with{" "}
              <strong>New app</strong>.
            </>
          }
        />
      ) : (
        <TableCard loading={isLoading}>
          <ScrollTable>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Access</TableCell>
                <TableCell align="center">Enabled</TableCell>
                <TableCell align="center">In nav</TableCell>
                <TableCell padding="checkbox" />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((app) => (
                <TableRow key={app.id} hover>
                  <TableCell>
                    <Tooltip title="Version history">
                      <Link
                        component="button"
                        variant="body2"
                        underline="hover"
                        onClick={() => setSelected(app)}
                        sx={{ fontWeight: 500, textAlign: "left" }}
                      >
                        {app.title}
                      </Link>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" component="code" sx={{ fontFamily: "monospace" }}>
                      {app.slug}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <ScopeChip tenantId={app.tenant_id} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={app.access_mode === "restricted" ? "warning" : "default"}
                      label={app.access_mode === "restricted" ? "Restricted" : "Everyone"}
                      onClick={() =>
                        update.mutate({
                          id: app.id,
                          body: {
                            access_mode:
                              app.access_mode === "restricted" ? "everyone" : "restricted",
                          },
                        })
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={app.enabled}
                      onChange={(e) =>
                        update.mutate({ id: app.id, body: { enabled: e.target.checked } })
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={app.show_in_nav}
                      onChange={(e) =>
                        update.mutate({ id: app.id, body: { show_in_nav: e.target.checked } })
                      }
                    />
                  </TableCell>
                  <TableCell padding="checkbox" align="right">
                    <RowActions
                      label={app.title}
                      primary={{
                        label: "Open app",
                        icon: <ExternalLink fontSize="small" />,
                        disabled: !app.current_version_id,
                        onClick: () => navigate(`/apps/${app.slug}`),
                      }}
                      actions={[
                        {
                          label: "Manage access",
                          icon: <Lock fontSize="small" />,
                          onClick: () => setGrantsFor(app),
                        },
                        {
                          label: app.tv_share_code ? `Shared to TV (${app.tv_share_code})` : "Share to TV",
                          icon: <Tv fontSize="small" color={app.tv_share_code ? "primary" : undefined} />,
                          onClick: () => setTvFor(app),
                        },
                        {
                          label: "Upload version",
                          icon: <Upload fontSize="small" />,
                          onClick: () => setUploadTarget(app),
                        },
                        {
                          label: "Delete",
                          icon: <Trash2 fontSize="small" />,
                          destructive: true,
                          onClick: () => remove.mutateAsync(app),
                          confirm: {
                            title: `Delete app: ${app.title}`,
                            body: (
                              <>
                                This removes the app and its entire version history. It cannot be
                                undone — disable it instead if you only want to take it out of
                                service.
                              </>
                            ),
                          },
                        },
                      ]}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ScrollTable>
        </TableCard>
      )}

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle>Version history: {selected?.title}</DialogTitle>
        <DialogContent dividers>
          {versionsLoading ? (
            <CenteredSpinner size={24} />
          ) : (
            <ScrollTable>
              <TableHead>
                <TableRow>
                  <TableCell>Version</TableCell>
                  <TableCell>Published</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>Queries</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {(versions ?? []).map((v) => {
                  const isLive = v.id === selected?.current_version_id;
                  return (
                    <TableRow key={v.id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                          <span>v{v.version}</span>
                          {isLive ? <Chip size="small" color="success" label="Live" /> : null}
                          {v.client_code ? (
                            <Chip size="small" variant="outlined" label="Custom code" />
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell><FormattedDate value={v.created_at} format="datetime" /></TableCell>
                      <TableCell>{v.notes ?? "—"}</TableCell>
                      <TableCell>
                        {Object.keys(v.manifest?.queries ?? {}).join(", ") || "—"}
                      </TableCell>
                      <TableCell align="right">
                        <DevOnly>
                          <Tooltip title="View code">
                            <IconButton size="small" onClick={() => setCodeVersion(v)}>
                              <FileCode2 fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </DevOnly>
                        {!isLive && selected ? (
                          <Button
                            size="small"
                            loading={update.isPending && update.variables?.body.current_version_id === v.id}
                            onClick={() =>
                              update.mutate({
                                id: selected.id,
                                body: { current_version_id: v.id },
                              })
                            }
                          >
                            Make live
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </ScrollTable>
          )}
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            Versions are immutable. “Make live” repoints the app at an existing version; nothing is
            rewritten.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <MicroAppCodeViewerDialog version={codeVersion} onClose={() => setCodeVersion(null)} />
      <MicroAppGrantsDialog app={grantsFor} onClose={() => setGrantsFor(null)} />
      <MicroAppTvShareDialog app={tvFor} onClose={() => setTvFor(null)} />
      {uploadTarget !== undefined ? (
        <MicroAppUploadDialog
          key={uploadTarget?.id ?? "new"}
          app={uploadTarget}
          onClose={() => setUploadTarget(undefined)}
          onDone={(warnings) => {
            setUploadTarget(undefined);
            toastUploadResult(warnings);
          }}
        />
      ) : null}

      <Box sx={{ mt: 4 }}>
        <MicroAppSheetsPanel />
      </Box>
    </Box>
  );
}

export default MicroAppsPage;
